import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Alert, Button, Skeleton, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import api from '../api';
import {WorkspaceAssetDetails} from '../widgets/AssetModal';
import {CUSTOM_META_FIELDS_CHANGED_EVENT} from '../helpers/customMetaFieldsEvents';

// The owner and scroller survive cursor navigation. Only the editors are keyed,
// so drafts belong to one asset while the comparison offset belongs to Info.
export default function WorkspaceAssetInfo({assetId, cachedAsset, organization, onNavigate}) {
    const {t} = useTranslation();
    const organizationId = organization?.id;
    const identity = `${organizationId}:${assetId}`;
    const liveIdentity = useRef(identity);
    liveIdentity.current = identity;
    const liveOrganization = useRef(organizationId);
    liveOrganization.current = organizationId;
    const [version, setVersion] = useState(0);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const generation = useRef(0);
    const mounted = useRef(false);
    const refresh = useCallback(() => setVersion(value => value + 1), []);
    const isCurrentOrganization = useCallback(id => mounted.current && liveOrganization.current === id, []);

    useEffect(() => {
        mounted.current = true;
        const invalidate = event => {
            if (event.type === CUSTOM_META_FIELDS_CHANGED_EVENT && event.detail?.ownerType && event.detail.ownerType !== 'Asset') return;
            if (event.type === 'mediagraph:dataRefresh' && !event.detail?.scopes?.includes('assets')) return;
            refresh();
        };
        window.addEventListener(CUSTOM_META_FIELDS_CHANGED_EVENT, invalidate);
        window.addEventListener('mediagraph:dataRefresh', invalidate);
        window.addEventListener('tagUpdated', refresh);
        return () => {
            mounted.current = false;
            ++generation.current;
            window.removeEventListener(CUSTOM_META_FIELDS_CHANGED_EVENT, invalidate);
            window.removeEventListener('mediagraph:dataRefresh', invalidate);
            window.removeEventListener('tagUpdated', refresh);
        };
    }, [refresh]);

    useEffect(() => {
        const request = ++generation.current;
        let active = true;
        const current = () => active && mounted.current && liveIdentity.current === identity && generation.current === request;
        setError(null);
        if (!assetId || !organizationId) { setDetail(null); return; }
        const config = {headers: {OrganizationId: organizationId}};
        api.get(`/api/assets/${assetId}`, config).then(async response => {
            if (!current()) return;
            const asset = response.data;
            if (asset.id !== assetId) throw new Error('Unexpected asset');
            // Definitions for CTA fields come only from authorized asset detail.
            setDetail(previous => ({identity, asset, contentVersion: request, customMetaFields: previous?.identity === identity ? previous.customMetaFields : []}));
            if (asset.entity_type) return;
            try {
                const fields = (await api.get('/api/custom_meta_fields', config)).data;
                if (current()) setDetail({identity, asset, contentVersion: request, customMetaFields: fields});
            } catch {
                if (current()) setDetail({identity, asset, contentVersion: request, customMetaFields: [], fieldsError: true});
            }
        }).catch(() => { if (current()) { setDetail(null); setError(identity); } });
        return () => { active = false; };
    }, [identity, assetId, organizationId, cachedAsset, version]);

    const saved = useCallback(() => {
        if (liveIdentity.current !== identity || !mounted.current) return;
        ++generation.current; // A pre-save fetch cannot replace the saved values.
        refresh();
    }, [identity, refresh]);

    const scroller = useRef(null);
    const contents = useRef(null);
    const desiredOffset = useRef(0);
    const appliedOffset = useRef(0);
    const contentHeight = useRef(0);
    const pending = !!assetId && detail?.identity !== identity;
    const [contentState, setContentState] = useState(null);
    const contentLoadingChanged = useCallback(loading => {
        if (mounted.current && liveIdentity.current === identity) setContentState({identity, loading});
    }, [identity]);
    const preserveHeight = pending || (!!assetId && !!detail?.asset.entity_type &&
        (contentState?.identity !== identity || contentState.loading));
    const livePreserveHeight = useRef(preserveHeight);
    livePreserveHeight.current = preserveHeight;
    const restoreScroll = useCallback(() => {
        const element = scroller.current;
        if (!element) return;
        const offset = Math.min(desiredOffset.current, Math.max(0, element.scrollHeight - element.clientHeight));
        appliedOffset.current = offset;
        element.scrollTop = offset;
    }, []);
    useLayoutEffect(() => {
        if (!preserveHeight) contentHeight.current = contents.current?.scrollHeight || 0;
        restoreScroll();
    }, [identity, detail, error, preserveHeight, restoreScroll]);
    useEffect(() => {
        if (!window.ResizeObserver) return;
        const observer = new ResizeObserver(() => {
            if (!livePreserveHeight.current) contentHeight.current = contents.current?.scrollHeight || 0;
            restoreScroll();
        });
        if (scroller.current) observer.observe(scroller.current);
        if (contents.current) observer.observe(contents.current);
        return () => observer.disconnect();
    }, [restoreScroll]);

    return <div data-testid="workspace-info-scroll" ref={scroller}
        style={{maxHeight: 'calc(100dvh - 220px)', overflowY: 'auto', overflowAnchor: 'none'}}
        onScroll={event => {
            const offset = event.currentTarget.scrollTop;
            if (Math.abs(offset - appliedOffset.current) > 1) {
                desiredOffset.current = offset;
                appliedOffset.current = offset;
            }
        }}>
        <div ref={contents} style={{minHeight: preserveHeight ? contentHeight.current : undefined}}>
            {!assetId ? <Typography.Text type="secondary">{t('highlight-asset-for-info', 'Highlight an asset to view its info.')}</Typography.Text>
                : error === identity ? <Alert type="error" message={t('asset-info-load-error', 'This asset could not be loaded.')}
                    action={<Button size="small" onClick={refresh}>{t('retry', 'Retry')}</Button>}/>
                    : pending ? <Skeleton active/>
                        : <WorkspaceAssetDetails key={identity} asset={detail.asset} organizationId={organizationId}
                            customMetaFields={detail.customMetaFields} fieldsError={detail.fieldsError}
                            refreshVersion={detail.contentVersion} onRefresh={saved} onRetry={refresh} onContentLoadingChange={contentLoadingChanged}
                            isCurrentOrganization={isCurrentOrganization} handleCancel={onNavigate || (() => {})}/>
            }
        </div>
    </div>;
}
